<template>
    <div class="withdraw-modal">
        <modal class="style-modal-withdraw" @close-modal="closeModal">
            <main class="mt-1">
                <div class="container overflow: hidden px-0">
                    <div class="withdraw-container px-3 py-4">
                        <!-- Wallet nav: segmented Deposit | Withdraw toggle (matches the
                             deposit modal). Tapping Deposit navigates back to it. -->
                        <div class="wallet-tabs">
                            <button type="button" class="wallet-tab" @click="switchToDeposit">Deposit</button>
                            <button type="button" class="wallet-tab wallet-tab--active">Withdraw</button>
                        </div>
                        <div class="row mx-0">
                            <div class="col-8 row align-items-center mx-0 flex-nowrap">
                                <img src="/images/lightningSquare.svg" class="wallet-header-icon mr-3 ml-0" />
                                <div class="wallet-header-text">
                                    <label class="withdraw-label-1">Withdraw Crypto</label>
                                    <label class="withdraw-label-2 withdraw-label-2--header">Min $10</label>
                                </div>
                            </div>
                            <div class="col-4 icons-container-aligment">
                                <div class="icons-container-aligment-item">
                                    <font-awesome-icon
                                        :icon="faBitcoin"
                                        class="mr-2 ml-0 font-weight-light bitcoinIcon"
                                        color="#f7931a"
                                    />
                                    <img
                                        :src="`/icons/usdt.png`"
                                        class="mr-2 ml-0 logosCoin"
                                    />
                                    <img
                                        :src="`/icons/usdc.png`"
                                        class="mr-2 ml-0 logosCoin"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <!-- Token Selection -->
                        <div class="row mx-0 pt-4 pb-3">
                            <div class="col-12">
                                <label class="withdraw-label-2">Select Token</label>
                                <div v-click-outside="() => isTokenOpen = false" class="dropdown-wrapper">
                                    <button class="dropdown" :class="{ 'dropdown-open': isTokenOpen }" @click="isTokenOpen = !isTokenOpen">
                                        <div class="dropdown-button">
                                            <div class="dropdown-selected">
                                                <font-awesome-icon
                                                    v-if="selectedToken.name === 'BITCOIN'"
                                                    :icon="faBitcoin"
                                                    class="dropdown-icon bitcoinIcon"
                                                    color="#f7931a"
                                                />
                                                <img
                                                    v-else
                                                    :src="`/icons/${selectedToken.name.toLowerCase()}.png`"
                                                    class="dropdown-icon logosCoin"
                                                />
                                                <span>{{ formatTokenName(selectedToken.name) }}</span>
                                            </div>
                                            <font-awesome-icon :icon="faChevronDown" class="dropdown-chevron" :class="{ 'chevron-up': isTokenOpen }" />
                                        </div>
                                    </button>
                                    <transition name="dropdown-fade">
                                        <div v-if="isTokenOpen" class="dropdown-content">
                                            <a v-for="token in tokens" :key="token.name" @click="selectToken(token)" class="dropdown-item" :class="{ 'dropdown-item-selected': token.name === selectedToken.name }">
                                                <font-awesome-icon
                                                    v-if="token.name === 'BITCOIN'"
                                                    :icon="faBitcoin"
                                                    class="dropdown-icon bitcoinIcon"
                                                    color="#f7931a"
                                                />
                                                <img
                                                    v-else
                                                    :src="`/icons/${token.name.toLowerCase()}.png`"
                                                    class="dropdown-icon logosCoin"
                                                />
                                                <span>{{ formatTokenName(token.name) }}</span>
                                            </a>
                                        </div>
                                    </transition>
                                </div>
                            </div>  
                        </div>
                        
                        <!-- Amount Input -->
                        <div class="row mx-0 pb-3">
                            <div class="col-12">
                                <label class="withdraw-label-2">
                                    Amount (USD)
                                    <span class="max-balance" @click="setMaxAmount">Max ${{ formatBalance(availableBalance) }}</span>
                                </label>
                                <div class="withdraw-input-container">
                                    <div class="input-with-prefix">
                                        <span class="input-prefix">$</span>
                                        <input 
                                            class="withdraw-input withdraw-input--with-prefix" 
                                            type="number" 
                                            v-model="withdrawAmount" 
                                            placeholder="0.00"
                                            min="10"
                                            :max="availableBalance"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Address Input -->
                        <div class="row mx-0 pb-3">
                            <div class="col-12">
                                <label class="withdraw-label-2">
                                    {{ selectedToken.name === 'BITCOIN' ? 'Bitcoin' : selectedToken.name === 'ETHEREUM' ? 'Ethereum' : selectedToken.name }} Address
                                    <span class="paste-button" @click="pasteAddress">Paste</span>
                                </label>
                                <div class="withdraw-input-container">
                                    <textarea 
                                        class="withdraw-input withdraw-input--address" 
                                        v-model="withdrawAddress" 
                                        :placeholder="`Enter your ${formatTokenName(selectedToken.name)} address`"
                                        rows="2"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Submit Button -->
                        <div class="row mx-0 pt-2">
                            <div class="col-12">
                                <div class="withdraw-button-container">
                                    <button class="withdraw-button" @click="submitWithdrawal" :disabled="isSubmitting">
                                        <font-awesome-icon :icon="faArrowUp" class="mr-2" />
                                        {{ isSubmitting ? 'Processing...' : 'Submit Withdrawal' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Info Box -->
                        <div class="row mx-0 pt-3">
                            <div class="col-12">
                                <div class="withdraw-info-box">
                                    <div class="withdraw-info-item">
                                        <font-awesome-icon :icon="faCircleInfo" class="withdraw-info-icon" />
                                        <span>Minimum withdrawal is $10</span>
                                    </div>
                                    <div class="withdraw-info-item">
                                        <font-awesome-icon :icon="faCircleInfo" class="withdraw-info-icon" />
                                        <span>Withdrawals are processed within 24 hours</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </modal>

        <!-- 2FA verification for a new withdrawal address. Shown when the gate
             returns requiresVerification; on success the backend has already
             submitted the withdrawal. -->
        <two-factor-verify
            v-if="verifyState.show"
            :request-id="verifyState.requestId"
            :method="verifyState.method"
            @verified="onVerified"
            @close-modal="onGateModalClosed"
        />

        <!-- 2FA enrollment, shown when the user has no factor yet (the gate
             returns requiresEnrollment). After enrolling we retry the withdrawal. -->
        <two-factor-enroll
            v-if="enrollState.show"
            source="withdrawal"
            @enrolled="onEnrolled"
            @close-modal="onGateModalClosed"
        />
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { validate } from 'bitcoin-address-validation';
import Modal from '@/components/global/Modal.vue';
import TwoFactorVerify from '@/components/account/modals/TwoFactorVerify.vue';
import TwoFactorEnroll from '@/components/account/modals/TwoFactorEnroll.vue';

// Simple ethereum address validation (replaces problematic ethereum-address package)
// Validates basic format: 0x followed by 40 hex characters
const ethValidate = (address) => {
  if (!address || typeof address !== 'string') return false;
  return /^0x[0-9a-fA-F]{40}$/.test(address);
};
import { emitter } from '@/core/emitter';
import { useAPI } from '@/composables/useAPI';
import { useUserStore } from '@/stores/user';
import { withdrawalRequestSuccess, withdrawalRequestFail } from '@/core/notifications';
import { PostHogUtils } from '@/core/PostHogUtils';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBitcoin } from '@fortawesome/free-brands-svg-icons/faBitcoin';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons/faCircleInfo';
import Swal from 'sweetalert2';

const userStore = useUserStore();

const isTokenOpen = ref(false);
const withdrawAmount = ref('');
const withdrawAddress = ref('');
const isSubmitting = ref(false);

// 2FA gate state. The new-address withdrawal gate returns one of:
//   requiresVerification -> prove the existing factor (verifyState)
//   requiresEnrollment   -> no factor yet, enroll first (enrollState), then retry
const verifyState = reactive({ show: false, requestId: '', method: 'email' });
const enrollState = reactive({ show: false });

const tokens = [
    { name: 'BITCOIN', key: 'btc' },
    { name: 'USDT', key: 'usdt' },
    { name: 'USDC', key: 'usdc' },
];

const selectedToken = ref(tokens[0]);

// Truncate to 2 decimals (mirrors the old cashier's fixed2 helper). Unlike
// toFixed, this floors rather than rounds, so the displayed max can never
// exceed the true withdrawable amount.
function fixed2(value) {
    if (value < 0) {
        return value < parseInt(value, 10) ? value.toFixed(2) : parseInt(value, 10);
    }
    return value > parseInt(value, 10) ? value.toFixed(2) : parseInt(value, 10);
}

// Withdrawable balance — ported verbatim from the old cashier
// (account/cashier/Withdrawal.vue). This is NOT the bet-spendable formula:
// withdrawals must never let a user pull out funds backed only by their credit
// line. In this store, liability is negative and creditLimit is a positive
// magnitude.
const available = computed(() => {
    return (
        Math.abs(userStore.user.displayBalance) -
        Math.abs(userStore.user.liability) +
        Math.abs(userStore.user.creditLimit)
    );
});

const displayBalanceComputed = computed(() => {
    // Once liability eats past the credit cushion, fall back to the
    // credit-adjusted available; otherwise the raw deposited balance is
    // withdrawable.
    if (Math.abs(userStore.user.creditLimit) < Math.abs(userStore.user.liability))
        return available.value;
    return userStore.user.displayBalance;
});

const availableBalance = computed(() => {
    return fixed2(parseFloat(displayBalanceComputed.value)) > 0
        ? fixed2(parseFloat(displayBalanceComputed.value))
        : 0;
});

function formatBalance(balance) {
    const num = parseFloat(balance);
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatTokenName(name) {
    if (name === 'BITCOIN') return 'Bitcoin';
    if (name === 'ETHEREUM') return 'Ethereum';
    if (name === 'USDT') return 'USDT (ERC-20)';
    if (name === 'USDC') return 'USDC (ERC-20)';
    return name;
}

function selectToken(token) {
    selectedToken.value = token;
    isTokenOpen.value = false;
}

function setMaxAmount() {
    withdrawAmount.value = availableBalance.value;
}

async function pasteAddress() {
    try {
        const text = await navigator.clipboard.readText();
        withdrawAddress.value = text;
    } catch (err) {
        console.error('Failed to read clipboard:', err);
    }
}

// The modal is mounted when opened (v-if), so mount = a withdrawal attempt begun.
onMounted(() => {
    PostHogUtils.capture('withdrawal_modal_opened');
});

async function submitWithdrawal() {
    // KYC gate: a user with verification required and an unapproved status must
    // complete Veriff before any withdrawal can be submitted. Mirrors the gate in
    // AccountInfo (deposit) and the legacy cashier Withdrawal.vue.
    const veriffData = userStore.user?.veriffData;
    if (veriffData?.required === true && veriffData?.status !== 'approved') {
        PostHogUtils.markKycRequired();
        PostHogUtils.capture('withdrawal_blocked', { reason: 'kyc' });
        emitter.emit('showVeriffModal');
        return;
    }

    const amount = parseFloat(withdrawAmount.value);

    if (!amount || amount < 10) {
        PostHogUtils.capture('withdrawal_blocked', { reason: 'min_amount' });
        Swal.fire({
            icon: 'error',
            title: 'Invalid Amount',
            text: 'Minimum withdrawal is $10',
        });
        return;
    }

    if (amount > availableBalance.value) {
        PostHogUtils.capture('withdrawal_blocked', { reason: 'insufficient' });
        Swal.fire({
            icon: 'error',
            title: 'Insufficient Balance',
            text: `Your maximum withdrawal is $${formatBalance(availableBalance.value)}`,
        });
        return;
    }

    if (!withdrawAddress.value) {
        PostHogUtils.capture('withdrawal_blocked', { reason: 'no_address' });
        Swal.fire({
            icon: 'error',
            title: 'Invalid Address',
            text: 'Please enter a withdrawal address',
        });
        return;
    }

    // Validate address format
    const currency = selectedToken.value?.name?.toLowerCase();
    const isBtcValid = currency === 'bitcoin' ? validate(withdrawAddress.value) : true;
    const isEthValid = ['ethereum', 'usdt', 'usdc'].includes(currency) ? ethValidate(withdrawAddress.value) : true;

    if (!isBtcValid) {
        PostHogUtils.capture('withdrawal_blocked', { reason: 'invalid_address', currency });
        Swal.fire({
            icon: 'error',
            title: 'Invalid Address',
            text: 'Please enter a valid Bitcoin address',
        });
        return;
    }

    if (!isEthValid) {
        PostHogUtils.capture('withdrawal_blocked', { reason: 'invalid_address', currency });
        Swal.fire({
            icon: 'error',
            title: 'Invalid Address',
            text: 'Please enter a valid Ethereum address',
        });
        return;
    }

    // Confirm withdrawal
    const result = await Swal.fire({
        html: '<h4>Are you sure you want to submit this withdrawal request?</h4>',
        showCancelButton: true,
        confirmButtonText: 'Confirm',
        confirmButtonColor: '#6f75b1',
    });

    if (!result.isConfirmed) {
        PostHogUtils.capture('withdrawal_blocked', { reason: 'unconfirmed' });
        return;
    }

    await submitToGate(amount);
}

// Hit the per-destination withdrawal gate. A new address comes back as
// requiresVerification (prove the factor) or requiresEnrollment (set 2FA up
// first); a trusted address submits straight through. Verifying/enrolling are
// the expected happy path here, not errors.
async function submitToGate(amount) {
    isSubmitting.value = true;
    try {
        const { request } = useAPI('cashier/withdrawal');
        const res = await request({
            amount,
            address: withdrawAddress.value,
            currency: selectedToken.value.name.toLowerCase(),
        });

        // Guarded read for analytics only — never let a null token throw into
        // the catch below and turn a successful gate into a false failure.
        const phCurrency = selectedToken.value?.name?.toLowerCase();

        if (res.requiresEnrollment) {
            PostHogUtils.capture('withdrawal_gate', { outcome: 'enroll_required', currency: phCurrency });
            enrollState.show = true;
            return;
        }

        if (res.requiresVerification) {
            PostHogUtils.capture('withdrawal_gate', { outcome: 'verify_required', currency: phCurrency });
            verifyState.requestId = res.requestId;
            verifyState.method = res.method || 'email';
            verifyState.show = true;
            return;
        }

        // Trusted destination — submitted straight through. Only treat it as a
        // success when the API actually says so; a 200 with success:false (e.g.
        // a domain failure) must surface as an error, not a false confirmation.
        if (res.success) {
            PostHogUtils.capture('withdrawal_gate', { outcome: 'trusted', currency: phCurrency });
            PostHogUtils.capture('withdrawal_completed', { currency: phCurrency });
            withdrawalRequestSuccess(res.message || 'Withdrawal request submitted successfully');
            closeModal();
        } else {
            PostHogUtils.capture('withdrawal_failed', { reason: 'domain' });
            withdrawalRequestFail(res.message || 'Withdrawal request failed');
        }
    } catch (error) {
        PostHogUtils.capture('withdrawal_failed', { reason: 'request_error' });
        withdrawalRequestFail(error.message || 'Withdrawal request failed');
    } finally {
        // Keep the form locked while a 2FA modal is open (the gate isn't done
        // yet) so the user can't fire a second cashier/withdrawal in parallel.
        if (!verifyState.show && !enrollState.show) {
            isSubmitting.value = false;
        }
    }
}

// The verify modal calls /auth/verify, which on success runs executeWithdrawal
// server-side — so by the time we're here the request is already submitted.
function onVerified() {
    verifyState.show = false;
    PostHogUtils.capture('withdrawal_completed', {
        currency: selectedToken.value?.name?.toLowerCase(),
    });
    withdrawalRequestSuccess('Withdrawal request submitted successfully');
    closeModal();
}

// After enrolling a factor, retry the same withdrawal — now it'll come back as
// requiresVerification (or submit, if the address ends up trusted).
async function onEnrolled() {
    enrollState.show = false;
    const amount = parseFloat(withdrawAmount.value);
    await submitToGate(amount);
}

// User dismissed a 2FA modal without completing the gate — release the form so
// they can try again (the button was held disabled while the modal was open).
function onGateModalClosed() {
    verifyState.show = false;
    enrollState.show = false;
    isSubmitting.value = false;
}

function closeModal() {
    withdrawAmount.value = '';
    withdrawAddress.value = '';
    selectedToken.value = tokens[0];
    verifyState.show = false;
    enrollState.show = false;
    emitter.emit('showWithdrawModal');
}

// Minimal withdraw -> deposit switch: close this modal, open the deposit one.
// Withdraw -> deposit tab switch: deterministic open deposit + close withdraw
// (no toggles; resets this modal's local form state first).
function switchToDeposit() {
    withdrawAmount.value = '';
    withdrawAddress.value = '';
    selectedToken.value = tokens[0];
    verifyState.show = false;
    enrollState.show = false;
    emitter.emit('walletSwitchToDeposit');
}
</script>

<style scoped lang="scss">
.withdraw-container {
    // Wallet palette — anchored on the brand indigo (#343b86) instead of the
    // old off-brand tailwind purple. Light values here, night overrides below.
    --wallet-accent: #343b86;
    --wallet-accent-deep: #262c66;
    --wallet-accent-soft: #6f75b1;
    --wallet-muted: #6e739b;
    --wallet-text-soft: #565c82;
    --wallet-card-bg: #f4f5fa;
    --wallet-chip-bg: rgba(52, 59, 134, 0.08);
    --wallet-chip-bg-hover: rgba(52, 59, 134, 0.16);
    --wallet-ring: rgba(52, 59, 134, 0.14);
    --wallet-track-bg: rgba(52, 59, 134, 0.07);

    background-color: var(--wallet-card-bg);
    border-radius: 12px;

    [theme='night'] & {
        --wallet-accent: #6e76c8;
        --wallet-accent-deep: #4d56ab;
        --wallet-accent-soft: #969ccb;
        --wallet-muted: #9aa0c3;
        --wallet-text-soft: #b6bad4;
        --wallet-card-bg: var(--grey-1600);
        --wallet-chip-bg: rgba(150, 156, 203, 0.16);
        --wallet-chip-bg-hover: rgba(150, 156, 203, 0.28);
        --wallet-ring: rgba(150, 156, 203, 0.22);
        --wallet-track-bg: rgba(0, 0, 0, 0.25);
    }
}

.wallet-tabs {
    display: flex;
    gap: 4px;
    padding: 4px;
    margin-bottom: 1.25rem;
    background: var(--wallet-track-bg);
    border-radius: 10px;
}

.wallet-tab {
    flex: 1;
    padding: 9px 12px;
    border: none;
    border-radius: 7px;
    background: transparent;
    color: var(--wallet-muted);
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    transition: background 0.18s ease, color 0.18s ease;

    &:hover:not(.wallet-tab--active) {
        color: var(--color-primary_01);
    }

    &--active {
        background: var(--white_01, #fff);
        color: var(--color-primary_01);
        font-weight: 700;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        cursor: default;
    }
}

// Header block: icon + stacked title/subtitle, vertically centered as a unit.
.wallet-header-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
}

.wallet-header-text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    // Tight optical gap between the two lines; no outer margins so the block
    // centers cleanly against the 40px icon.
    gap: 1px;

    .withdraw-label-1,
    .withdraw-label-2--header {
        margin: 0;
        padding: 0;
    }
}

.withdraw-label-1 {
    display: block;
    font-size: 18px;
    // Roboto Slab only ships 300/400/700 — 500/600 were faux-bolded.
    font-weight: 700;
    line-height: 1.2;
    color: var(--color-primary_01);
    margin-bottom: 0;
}

// The header subtitle ("Min $10") is a plain line — override the form-field
// label layout (flex + space-between, used for the Max/Paste chip rows).
.withdraw-label-2--header {
    display: block;
    width: auto;
    line-height: 1.2;
    margin-bottom: 0;
}

.withdraw-label-2 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-size: 12px;
    font-weight: 400;
    color: var(--wallet-muted);
    margin-bottom: 6px;
}

.max-balance,
.paste-button {
    margin-left: 0.5rem;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--wallet-chip-bg);
    color: var(--wallet-accent);
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: var(--wallet-chip-bg-hover);
    }
}

.icons-container-aligment {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: end;
}

.icons-container-aligment-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
}

.bitcoinIcon {
    font-size: 16px;
}

.logosCoin {
    width: 16px;
}

.dropdown-wrapper {
    position: relative;
}

.dropdown {
    width: 100%;
    background: var(--white_01);
    background-image: none !important;
    border: 1px solid var(--grey-1200-1);
    border-radius: 10px;
    padding: 10px 14px;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        border-color: var(--wallet-accent-soft);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.dropdown-open {
    border-color: var(--wallet-accent-soft);
    box-shadow: 0 0 0 3px var(--wallet-ring);
    background: var(--white_01);
}

.dropdown-button {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    text-decoration: none;
    color: var(--color-primary_01);
}

.dropdown-selected {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 400;
}

.dropdown-icon {
    width: 20px;
    height: 20px;
}

.dropdown-chevron {
    font-size: 12px;
    color: var(--grey-2200);
    transition: transform 0.2s ease;
}

.chevron-up {
    transform: rotate(180deg);
}

.dropdown-content {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background-color: var(--white_01);
    border: 1px solid var(--grey-1200-1);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    z-index: 100;
    overflow: hidden;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    color: var(--color-primary_01);
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: var(--wallet-chip-bg);
    }

    &.dropdown-item-selected {
        background-color: var(--wallet-chip-bg);
        font-weight: 700;
    }
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
    transition: opacity 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
    opacity: 0;
}

.withdraw-input-container {
    width: 100%;
}

.input-with-prefix {
    position: relative;
    display: flex;
    align-items: center;
}

.input-prefix {
    position: absolute;
    left: 14px;
    color: var(--wallet-muted);
    font-size: 15px;
    font-weight: 400;
    pointer-events: none;
    z-index: 1;
}

.withdraw-input {
    width: 100%;
    border: 1px solid var(--grey-1200-1);
    border-radius: 10px;
    padding: 13px 16px;
    background: var(--white_01);
    color: var(--color-primary_01);
    font-size: 16px;
    font-weight: 400;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
        color: var(--grey-2200);
        font-weight: 400;
    }

    &:focus {
        outline: none;
        border-color: var(--wallet-accent-soft);
        box-shadow: 0 0 0 3px var(--wallet-ring);
    }

    // Amount: prominent numerals, no native spinner arrows.
    &[type='number'] {
        font-size: 17px;
        font-weight: 700;
        appearance: textfield;
        -moz-appearance: textfield;

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }

    &--with-prefix {
        padding-left: 30px;
    }

    &--address {
        resize: none;
        min-height: 60px;
        word-break: break-all;
        overflow-wrap: break-word;
        font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
        font-size: 13px;
        line-height: 1.6;
        letter-spacing: 0.02em;

        // The placeholder is prose, not an address — keep it in the site font.
        &::placeholder {
            font-family: 'Roboto Slab', Arial, sans-serif;
            font-size: 14px;
            letter-spacing: 0;
        }
    }
}

.withdraw-button-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
}

.withdraw-button {
    width: 100%;
    border: none;
    border-radius: 12px;
    padding: 14px 20px;
    background: linear-gradient(160deg, var(--wallet-accent) 0%, var(--wallet-accent-deep) 100%);
    color: white;
    font-weight: 700;
    font-size: 15px;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
    box-shadow: 0 4px 12px rgba(38, 44, 102, 0.22);
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(38, 44, 102, 0.3);
        filter: brightness(1.06);
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 2px 8px rgba(38, 44, 102, 0.22);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.withdraw-info-box {
    background: rgba(52, 59, 134, 0.05);
    border: 1px solid rgba(52, 59, 134, 0.12);
    border-radius: 12px;
    padding: 14px 18px;

    [theme='night'] & {
        background: rgba(150, 156, 203, 0.08);
        border-color: rgba(150, 156, 203, 0.22);
    }
}

.withdraw-info-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 0;
    color: var(--wallet-text-soft);
    font-size: 12.5px;
    font-weight: 400;

    &:first-child {
        padding-top: 0;
    }

    &:last-child {
        padding-bottom: 0;
    }
}

.withdraw-info-icon {
    font-size: 14px;
    color: var(--wallet-accent-soft);
}
</style>
